import { useState } from 'react';
import type { FormEvent } from 'react';
import { solicitarAcesso } from '../../acesso';
import type { Falha } from '../../acesso';
import { Icone } from '../../design/Icone';
import { propsDeSenhaQueNaoSalva } from '../../design/semSalvarSenha';

/**
 * TELA 04 — "SOLICITAR ACESSO" e "SOLICITAÇÃO ENVIADA".
 *
 * Os campos do design (nome, WhatsApp, código se possuir, função solicitada fixa em Balconista) e
 * UM a mais, que o design não desenhou mas a regra exige: a SENHA, escolhida pela própria pessoa.
 * O administrador nunca vê nem cria senha de ninguém (tela 31, "sem senha visível"); ela vai para
 * o servidor, vira hash na hora e, na aprovação, vira a credencial da conta.
 *
 * 🚨 A resposta do servidor é sempre a mesma — ela não diz se o código já existe (seria um oráculo
 * para descobrir quem trabalha na loja). Por isso a tela também não promete nada além de "recebido".
 */

const MINIMO_DA_SENHA = 6;

export function SolicitarAcesso({ aoVoltar }: { aoVoltar: () => void }) {
  const [nome, setNome] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [codigo, setCodigo] = useState('');
  const [senha, setSenha] = useState('');
  const [repetida, setRepetida] = useState('');
  const [mostrar, setMostrar] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<{ campo: string | null; texto: string } | null>(null);
  const [enviada, setEnviada] = useState(false);

  async function enviar(e: FormEvent) {
    e.preventDefault();
    if (enviando) return;
    const digitos = whatsapp.replace(/\D+/g, '');
    if (nome.trim().split(/\s+/).length < 2)
      return setErro({
        campo: 'nome',
        texto: 'Informe o nome completo, como aparece no cadastro.',
      });
    if (digitos.length < 10 || digitos.length > 11)
      return setErro({ campo: 'whatsapp', texto: 'WhatsApp com DDD: (32) 9 9999-9999.' });
    if (codigo && !/^[0-9A-Za-z]{1,20}$/.test(codigo.trim()))
      return setErro({ campo: 'codigo', texto: 'O código só tem letras e números.' });
    if ([...senha].length < MINIMO_DA_SENHA)
      return setErro({
        campo: 'senha',
        texto: `A senha precisa de pelo menos ${String(MINIMO_DA_SENHA)} caracteres.`,
      });
    if (senha !== repetida) return setErro({ campo: 'repetida', texto: 'As senhas não conferem.' });

    setEnviando(true);
    setErro(null);
    const r = await solicitarAcesso({
      nome: nome.trim(),
      whatsapp: digitos,
      codigo: codigo.trim(),
      senha,
    });
    setEnviando(false);
    if (r.ok) {
      setSenha('');
      setRepetida('');
      setEnviada(true);
      return;
    }
    setErro({ campo: null, texto: textoDaFalha(r.falha) });
  }

  if (enviada) {
    return (
      <div className="lg-form lg-enviada" role="status">
        <div className="lg-enviada-selo" aria-hidden="true">
          <Icone nome="check" tamanho={26} traco={2.2} />
        </div>
        <h1 className="lg-titulo">Solicitação enviada</h1>
        <p className="lg-sub">
          Um administrador autorizado analisará seu acesso. Depois da aprovação, você entra com o
          código confirmado por ele e a senha que acabou de criar.
        </p>
        <button type="button" className="lg-solicitar lg-voltar" onClick={aoVoltar}>
          Voltar para o login
        </button>
      </div>
    );
  }

  const classe = (campo: string) => `lg-campo${erro?.campo === campo ? ' lg-campo-erro' : ''}`;

  return (
    <form className="lg-form" autoComplete="off" onSubmit={(e) => void enviar(e)} noValidate>
      <h1 className="lg-titulo">Solicitar acesso</h1>
      <p className="lg-sub">Sua solicitação será analisada por um administrador autorizado.</p>

      <div className="lg-alerta-lugar lg-alerta-lugar-curto" aria-live="assertive">
        {erro && (
          <div className="lg-alerta" role="alert">
            <Icone nome="warn" tamanho={17} traco={1.7} />
            <div>
              <div className="lg-alerta-titulo">{erro.texto}</div>
            </div>
          </div>
        )}
      </div>

      <div className="lg-campos lg-campos-pedido">
        <div>
          <label className="lg-rotulo" htmlFor="sa-nome">
            Nome completo
          </label>
          <div className={classe('nome')}>
            <Icone nome="user" />
            <input
              id="sa-nome"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="Como aparece no cadastro"
              autoComplete="name"
              maxLength={120}
              disabled={enviando}
            />
          </div>
        </div>
        <div>
          <label className="lg-rotulo" htmlFor="sa-whats">
            WhatsApp
          </label>
          <div className={classe('whatsapp')}>
            <Icone nome="phone" />
            <input
              id="sa-whats"
              value={whatsapp}
              onChange={(e) => setWhatsapp(mascaraDeWhatsapp(e.target.value))}
              placeholder="(00) 0 0000-0000"
              inputMode="tel"
              autoComplete="tel-national"
              disabled={enviando}
            />
          </div>
        </div>
        <div>
          <label className="lg-rotulo" htmlFor="sa-codigo">
            Código, se possuir
          </label>
          <div className={classe('codigo')}>
            <Icone nome="hash" />
            <input
              id="sa-codigo"
              value={codigo}
              onChange={(e) => setCodigo(e.target.value)}
              placeholder="Opcional"
              autoCapitalize="none"
              spellCheck={false}
              maxLength={20}
              disabled={enviando}
            />
          </div>
        </div>
        <div>
          <span className="lg-rotulo">Função solicitada</span>
          <div className="lg-campo lg-campo-fixo">
            <Icone nome="brief" />
            <span>Balconista</span>
          </div>
        </div>
        <div>
          <label className="lg-rotulo" htmlFor="sa-senha">
            Crie sua senha
          </label>
          <div className={classe('senha')}>
            <Icone nome="lock" />
            <input
              id="sa-senha"
              {...propsDeSenhaQueNaoSalva(mostrar)}
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              placeholder={`Pelo menos ${String(MINIMO_DA_SENHA)} caracteres`}
              disabled={enviando}
            />
            <button
              type="button"
              className="lg-olho"
              onClick={() => setMostrar((v) => !v)}
              aria-label={mostrar ? 'Ocultar senha' : 'Mostrar senha'}
              aria-pressed={mostrar}
            >
              <Icone nome="eye" />
            </button>
          </div>
          <div className="lg-dica">Uma frase fica fácil de lembrar. Só você vai saber.</div>
        </div>
        <div>
          <label className="lg-rotulo" htmlFor="sa-repetida">
            Repita a senha
          </label>
          <div className={classe('repetida')}>
            <Icone nome="lock" />
            <input
              id="sa-repetida"
              {...propsDeSenhaQueNaoSalva(mostrar)}
              value={repetida}
              onChange={(e) => setRepetida(e.target.value)}
              disabled={enviando}
            />
          </div>
        </div>
      </div>

      <button className="lg-entrar lg-entrar-pedido" type="submit" disabled={enviando}>
        {enviando ? (
          <span className="lg-entrando">
            <span className="lg-spin" aria-hidden="true" />
            Enviando…
          </span>
        ) : (
          <>
            Enviar solicitação
            <Icone nome="arrow" />
          </>
        )}
      </button>
      <button type="button" className="lg-solicitar lg-voltar" onClick={aoVoltar}>
        Voltar para o login
      </button>
    </form>
  );
}

function textoDaFalha(falha: Falha): string {
  switch (falha.tipo) {
    case 'invalido':
      return falha.mensagem || 'Confira os dados e tente novamente.';
    case 'limite':
      return 'Muitas solicitações seguidas. Aguarde alguns minutos.';
    case 'offline':
      return 'Não foi possível conectar. Verifique sua conexão.';
    default:
      return 'Não foi possível enviar agora. Tente novamente.';
  }
}

/** "32999994410" → "(32) 9 9999-4410", enquanto digita. */
function mascaraDeWhatsapp(valor: string): string {
  const d = valor.replace(/\D+/g, '').slice(0, 11);
  if (d.length <= 2) return d.length ? `(${d}` : '';
  if (d.length <= 3) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 7) return `(${d.slice(0, 2)}) ${d.slice(2, 3)} ${d.slice(3)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 3)} ${d.slice(3, 7)}-${d.slice(7)}`;
}
